/**
 * Creates a smart field changed checker that handles:
 * 1. Early exit if field is not editable
 * 2. Column-level override (boolean or custom function)
 * 3. Fallback to default isFieldChanged logic
 *
 * @param {Object} params
 * @param {boolean} params.editable - Whether the field is editable
 * @param {boolean|Function} params.isFieldChangedOverride - Override: true (always), false (never), or custom function
 * @param {Function} params.defaultIsFieldChanged - Default change detection function (rowData, field) => boolean
 * @param {string} params.mapsToField - Field name
 * @param {Object} params.originalData - Original data for custom override function
 * @returns {Function} Enhanced field changed checker (rowData) => boolean
 */
export const createFieldChangedChecker = ({
    editable,
    isFieldChangedOverride,
    defaultIsFieldChanged,
    mapsToField,
    originalData
}) => {
    return (rowData) => {
        // Performance optimization: Non-editable fields can't change
        if (!editable) {
            return false
        }

        // Handle column-level override
        if (isFieldChangedOverride !== undefined) {
            // Boolean override: true = always changed, false = never changed
            if (typeof isFieldChangedOverride === 'boolean') {
                return isFieldChangedOverride
            }

            // Function override: custom comparison logic
            if (typeof isFieldChangedOverride === 'function') {
                // Find original row data for comparison
                if (!originalData) return false

                const originalRowData = Array.isArray(originalData)
                    ? originalData.find(row => row[rowData.id] === rowData.id)
                    : Object.values(originalData).find(row => {
                        // Try to match by any key that looks like an ID
                        const possibleKeys = Object.keys(rowData).filter(k =>
                            k.toLowerCase().includes('id') ||
                            k === 'sapLineNum' ||
                            k === rowData.id
                        )
                        return possibleKeys.some(key => row[key] === rowData[key])
                    })

                if (!originalRowData) return false

                // Call custom override function
                return isFieldChangedOverride(originalRowData, rowData)
            }
        }

        // Fallback to default isFieldChanged logic
        return defaultIsFieldChanged(rowData, mapsToField)
    }
}
